import {
    signIn as signInV6,
    signOut as signOutV6,
    getCurrentUser,
    fetchAuthSession,
    confirmSignIn as confirmSignInV6,
    confirmSignUp as confirmSignUpV6,
    fetchMFAPreference,
    updateMFAPreference,
    setUpTOTP,
    verifyTOTPSetup
} from 'aws-amplify/auth';
import awsmobile from '@/aws-exports';

let cachedUsername = '';
let pendingSetupDetails = null;

/**
 * Cognito (cả v2 lẫn v6) ghi username đang đăng nhập vào localStorage với cùng format key.
 * Đây là nguồn ĐỒNG BỘ duy nhất còn lại ở v6 - cần thiết vì 4 call site đọc username ngay
 * trong thân render, trước khi getCurrentUser() kịp resolve.
 */
const lastAuthUserKey = () => `CognitoIdentityServiceProvider.${awsmobile.aws_user_pools_web_client_id}.LastAuthUser`;

const readLastAuthUser = () => {
    try {
        return localStorage.getItem(lastAuthUserKey()) || '';
    } catch (e) {
        return '';
    }
};

/**
 * @todoAmplifyV6 Approach (B) - temporary workaround, to be removed in P7.
 *
 * Amplify v6 DOES NOT expose the refresh token via any public API (an intentional design decision).
 * However, extended/S3-CloudFront-Lambda/index.js:395,567 uses it with REFRESH_TOKEN_AUTH to
 * renew file access permissions via CloudFront; sending null causes users to lose view access after ~1h.
 *
 * Cognito still writes the refresh token to localStorage using the same key format in both v2 and v6.
 * This is an implicit contract with Cognito internals, NOT a public API: if AWS changes the key format,
 * it will break silently.
 * Therefore, a test locks down the key format against v6's actual DefaultTokenStore 
 * (test/src/amplify/auth.test.js) and a warning is logged if it reads back empty.
 *
 * The username in the key is the Cognito-returned username (ChallengeParameters.USERNAME, stored
 * in LastAuthUser), NOT the string entered in the login field — so LastAuthUser should be prioritized,
 * with cachedUsername used only as a fallback.
 */
export const readRefreshTokenFromStorage = () => {
    const username = readLastAuthUser() || cachedUsername;
    if (!username) {
        return '';
    }
    try {
        const key = `CognitoIdentityServiceProvider.${awsmobile.aws_user_pools_web_client_id}.${username}.refreshToken`;
        const token = localStorage.getItem(key) || '';
        if (!token) {
            console.warn('[amplify] không đọc được refreshToken từ localStorage - cookie CloudFront sẽ hết hiệu lực sau ~1h');
        }
        return token;
    } catch (e) {
        return '';
    }
};

const V6_STEP_TO_V2_CHALLENGE = {
    CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED: 'NEW_PASSWORD_REQUIRED',
    CONFIRM_SIGN_IN_WITH_TOTP_CODE: 'SOFTWARE_TOKEN_MFA',
    CONFIRM_SIGN_IN_WITH_SMS_CODE: 'SMS_MFA',
    CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE: 'CUSTOM_CHALLENGE',
    CONFIRM_SIGN_UP: 'CONFIRM_SIGN_UP',
    RESET_PASSWORD: 'RESET_PASSWORD',
    // Hai bước dưới đây v2 KHÔNG bao giờ sinh ra. Với Cognito MFA = ON, v6 trả
    // CONTINUE_SIGN_IN_WITH_TOTP_SETUP ngay lần đăng nhập đầu, trong khi v2 trả DONE rồi app
    // tự gọi setupTOTP. Nhánh default của các switch trong luồng login phải xử lý chúng.
    CONTINUE_SIGN_IN_WITH_TOTP_SETUP: 'MFA_SETUP',
    CONTINUE_SIGN_IN_WITH_MFA_SELECTION: 'SELECT_MFA_TYPE',
    CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION: 'MFA_SETUP',
    DONE: undefined
};

const toV2SignInResult = (result, username) => {
    if (username) {
        cachedUsername = username;
    }
    // Với bước CONTINUE_SIGN_IN_WITH_TOTP_SETUP, v6 trả sẵn secret trong nextStep. Phải dùng
    // cái này chứ KHÔNG được gọi setUpTOTP() riêng: lúc đó user chưa đăng nhập xong nên
    // setUpTOTP() sẽ fail.
    if (result?.nextStep?.totpSetupDetails) {
        pendingSetupDetails = result.nextStep.totpSetupDetails;
    }
    return {
        isSignedIn: result?.isSignedIn,
        nextStep: result?.nextStep,
        challengeName: V6_STEP_TO_V2_CHALLENGE[result?.nextStep?.signInStep],
        username: cachedUsername,
        getUsername: () => cachedUsername
    };
};

// v6 dùng err.name; 5 switch + 2 if trong luồng login đang đọc err.code.
const rethrowV2Error = err => {
    if (err && !err.code && err.name) {
        err.code = err.name;
    }
    throw err;
};

/**
 * v2 signIn khi đã có session thì thay thế session. v6 ném UserAlreadyAuthenticatedException
 * ngay lập tức - làm chết DialogConfirmPassword.js:25 (dùng signIn để xác thực lại mật khẩu).
 * Sign out trước để tái tạo ngữ nghĩa v2.
 *
 * KHÁC BIỆT CÒN LẠI: nếu mật khẩu sai, v2 giữ session cũ còn ở đây user đã bị signOut.
 * DialogConfirmPassword cần refactor thật (xem plan §6.3).
 */
const signIn = async (username, password) => {
    try {
        await getCurrentUser();
        await signOutV6();
    } catch (e) {
        /* chưa đăng nhập - đúng như mong đợi */
    }

    try {
        const result = await signInV6({ username, password });
        return toV2SignInResult(result, username);
    } catch (err) {
        return rethrowV2Error(err);
    }
};

const signOut = async () => {
    cachedUsername = '';
    pendingSetupDetails = null;
    return signOutV6();
};

const currentAuthenticatedUser = async () => {
    const user = await getCurrentUser();
    if (user?.username) {
        cachedUsername = user.username;
    }
    return { ...user, getUsername: () => user?.username };
};

/**
 * v2 currentSession() NÉM LỖI khi chưa đăng nhập; v6 fetchAuthSession() không ném mà trả
 * { tokens: undefined }. Phải ném lại để giữ nguyên ngữ nghĩa .catch() ở
 * userService.js:871 và authenService.js:297 (phát hiện "logout ở tab khác").
 */
const currentSession = async () => {
    const session = await fetchAuthSession();
    if (!session?.tokens) {
        throw new Error('No current user');
    }

    const wrap = token => {
        const jwtToken = token?.toString() ?? '';
        return { jwtToken, getJwtToken: () => jwtToken, payload: token?.payload };
    };

    return {
        getIdToken: () => wrap(session.tokens.idToken),
        getAccessToken: () => wrap(session.tokens.accessToken),
        getRefreshToken: () => ({ token: readRefreshTokenFromStorage() })
    };
};

const confirmSignUp = async (username, code) => {
    try {
        return await confirmSignUpV6({ username, confirmationCode: code });
    } catch (err) {
        return rethrowV2Error(err);
    }
};

// v6 bỏ hẳn tham số user - shim vẫn nhận để 4 call site không phải sửa.
const completeNewPassword = async (user, password) => {
    try {
        const result = await confirmSignInV6({ challengeResponse: password });
        return toV2SignInResult(result);
    } catch (err) {
        return rethrowV2Error(err);
    }
};

const confirmSignIn = async (user, code) => {
    try {
        const result = await confirmSignInV6({ challengeResponse: code });
        return toV2SignInResult(result);
    } catch (err) {
        return rethrowV2Error(err);
    }
};

// v2 trả string TOTP | NOMFA | SMS_MFA; v6 trả { enabled: Set, preferred }.
const getPreferredMFA = async () => {
    try {
        const { preferred } = await fetchMFAPreference();
        return preferred === 'TOTP' ? 'TOTP' : 'NOMFA';
    } catch (err) {
        return 'NOMFA';
    }
};

const setPreferredMFA = async (user, mfaMethod) => {
    if (mfaMethod === 'NOMFA') {
        return updateMFAPreference({ totp: 'DISABLED' });
    }
    return updateMFAPreference({ totp: 'PREFERRED' });
};

const setupTOTP = async () => {
    // Nếu đang ở giữa luồng sign-in với challenge TOTP setup thì secret đã có sẵn từ signIn.
    if (!pendingSetupDetails) {
        pendingSetupDetails = await setUpTOTP();
    }
    if (pendingSetupDetails?.username) {
        cachedUsername = pendingSetupDetails.username;
    }
    return pendingSetupDetails.sharedSecret;
};

/**
 * v2 chạy verifyTotpToken và setPreferredMFA song song bằng Promise.all và thường vẫn qua nhờ
 * may mắn về thứ tự. v6 thì updateMFAPreference FAIL nếu TOTP chưa verify
 * (InvalidParameterException), nên thứ tự bắt buộc phải tuần tự. Việc gọi đúng thứ tự do
 * totpHandler.js đảm nhiệm - xem plan §6.4(b).
 */
const verifyTotpToken = async (user, challengeAnswer) => {
    try {
        return await verifyTOTPSetup({ code: challengeAnswer });
    } catch (err) {
        return rethrowV2Error(err);
    }
};

export const getSetupUri = (issuer, username) => pendingSetupDetails?.getSetupUri?.(issuer, username || cachedUsername);

export const getCachedUsername = () => cachedUsername || readLastAuthUser();

export default {
    signIn,
    signOut,
    currentAuthenticatedUser,
    currentSession,
    confirmSignUp,
    completeNewPassword,
    confirmSignIn,
    getPreferredMFA,
    setPreferredMFA,
    setupTOTP,
    verifyTotpToken,
    getCachedUsername,
    getSetupUri,
    readRefreshTokenFromStorage
};
